import { path, equals } from 'ramda';
import { IOfferState } from '../../../../shared/redux/reducers';
import { getLocationPathFromAddress, isObjectSplits } from '../../../../shared/utils';
import { getIsCommercial } from '../../../../shared/utils/category/getIsCommercial';

const getJkId = (offer: IOfferState) => path(['geo', 'jk', 'id'], offer);
const getJkHouseId = (offer: IOfferState) => path(['geo', 'jk', 'house', 'id'], offer);
const getBusinessCenterId = (offer: IOfferState) => path(['businessShoppingCenter', 'id'], offer);
const getPhotosCount = (offer: IOfferState) => path(['photos', 'length'], offer);
const getCoordinates = (offer: IOfferState) => path(['geo', 'coordinates'], offer);

/**
 * Возвращает признак того, нужно ли загрузить новое ФВУ в ответ на обновление данных объявления.
 * Поскольку условий много, и отправлять для каждого изменения экшен накладно с точки зрения ререндера,
 * было принято решение отправлять всего 1 экшен.
 */
export function shouldLoadPublishTermsAfterOfferUpdate(
  offer: IOfferState = {} as IOfferState,
  prevOffer: IOfferState = {} as IOfferState,
  isExtraFieldsUpdated: boolean,
): boolean {
  if (equals(offer, prevOffer) && !isExtraFieldsUpdated) {
    return false;
  }

  const isOfferIdChanged = offer.id !== prevOffer.id;
  const isCategoryChanged = offer.category !== prevOffer.category;
  const isHomeownerChanged = offer.isByHomeowner !== prevOffer.isByHomeowner;
  const isPhotosCountChanged = getPhotosCount(offer) !== getPhotosCount(prevOffer);
  const isJkIdChanged = getJkId(offer) !== getJkId(prevOffer);
  const isJkHouseIdChanged = getJkHouseId(offer) !== getJkHouseId(prevOffer);
  const isBusinessCenterIdChanged = getBusinessCenterId(offer) !== getBusinessCenterId(prevOffer);
  const isObjectSplitsChanged = isObjectSplits(offer) !== isObjectSplits(prevOffer);
  const isTotalAreaChanged = offer.totalArea !== prevOffer.totalArea;
  const isPriceChanged = offer.bargainTerms?.price !== prevOffer.bargainTerms?.price;
  const isCurrencyChanged = offer.bargainTerms?.currency !== prevOffer.bargainTerms?.currency;
  const isPriceTypeChanged = offer.bargainTerms?.priceType !== prevOffer.bargainTerms?.priceType;
  const isPaymentPeriodChanged = offer.bargainTerms?.paymentPeriod !== prevOffer.bargainTerms?.paymentPeriod;
  const isMultiAdsSlotsCountChanged = offer.areaParts?.length !== prevOffer.areaParts?.length;

  const isLocationPathChanged = !equals(getLocationPathFromAddress(offer), getLocationPathFromAddress(prevOffer));
  const isCoordinatesChanged = !equals(getCoordinates(offer), getCoordinates(prevOffer));

  const shouldLoadPublishTermsGeneral =
    isOfferIdChanged ||
    isCategoryChanged ||
    isHomeownerChanged ||
    isLocationPathChanged ||
    isCoordinatesChanged ||
    isPhotosCountChanged ||
    isJkIdChanged ||
    isJkHouseIdChanged ||
    isBusinessCenterIdChanged ||
    isObjectSplitsChanged ||
    isExtraFieldsUpdated;

  const isCommercial = !!offer.category && getIsCommercial(offer.category);

  if (isCommercial) {
    return (
      shouldLoadPublishTermsGeneral ||
      isTotalAreaChanged ||
      isPriceChanged ||
      isCurrencyChanged ||
      isPriceTypeChanged ||
      isPaymentPeriodChanged ||
      isMultiAdsSlotsCountChanged
    );
  }

  return shouldLoadPublishTermsGeneral;
}
